import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridFilteredSortedRowIdsSelector,
  GridHeaderCheckbox,
  GridToolbarDensitySelector,
  GridToolbarExport,
  selectedIdsLookupSelector,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { alpha, Button, Grid, Theme, Tooltip } from "@mui/material";
import React from "react";
import { useTheme } from "@mui/material/styles";
import ResetIcon from "../../../theme/ResetIcon";

const MuiDataGridToolbarStyles = (theme: Theme) => ({
  toolbar: {
    position: "relative",
    width: "100%",
    borderBottom: "0.5px solid" + theme.palette.table.border,
    color: theme.palette.text.secondary,
  },
  highlight:
    theme.palette.mode === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: alpha(theme.palette.secondary.main, 0.12),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.secondary.main, 0.2),
        },
});
export default function MuiDataGridToolBar({
  customToolbar,
  checkboxSelection,
  saveUserConfig,
  treeData,
  handleResetTableView,
  isDefaultView,
  viewSelect,
  rowDensityMenu,
  exportMenu,
}: {
  customToolbar: () => React.ReactElement;
  checkboxSelection: boolean | undefined;
  saveUserConfig: boolean;
  treeData: boolean | undefined;
  handleResetTableView: () => void;
  isDefaultView: boolean;
  viewSelect: () => React.ReactElement;
  rowDensityMenu: boolean;
  exportMenu: boolean;
}) {
  const theme = useTheme();
  const styles = MuiDataGridToolbarStyles(theme);
  const toolbarApiRef = useGridApiContext();
  const selectedRows = toolbarApiRef.current.getSelectedRows();

  const treeDataHeaderCheckbox = () => {
    let data: {
      indeterminate?: boolean;
      checked?: boolean;
      onClick?: (e: MouseEvent) => void;
    } = {};
    if (treeData) {
      const children = gridFilteredSortedRowIdsSelector(
        toolbarApiRef.current.state,
        toolbarApiRef.current.instanceId
      ).filter((id) => !id.toString().includes("auto-generated"));

      const selectionLookup = selectedIdsLookupSelector(
        toolbarApiRef.current.state,
        toolbarApiRef.current.instanceId
      );

      const indeterminate =
        children?.some((child) => selectionLookup[child] === undefined) &&
        children?.some((child) => selectionLookup[child] !== undefined);

      const checked =
        toolbarApiRef.current.getAllRowIds().length !== 0 &&
        children?.every((child) => selectionLookup[child] !== undefined);
      data = {
        onClick: (e) => {
          toolbarApiRef.current.selectRows(children, !checked);
          e.preventDefault();
        },
        indeterminate,
        checked,
      };

      return data;
    }
  };

  if (!customToolbar && !saveUserConfig) return <></>;
  return (
    <Grid
      container
      flexWrap={"nowrap"}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
      sx={[styles.toolbar, selectedRows.size > 0 && styles.highlight]}
    >
      <Grid
        item
        xs={"auto"}
        container
        alignItems={"center"}
        sx={{ padding: 0.7, paddingRight: 0 }}
      >
        {checkboxSelection && (
          <GridHeaderCheckbox
            field={GRID_CHECKBOX_SELECTION_COL_DEF.field}
            colDef={{ ...GRID_CHECKBOX_SELECTION_COL_DEF, computedWidth: 100 }}
            {...treeDataHeaderCheckbox()}
          />
        )}
        {viewSelect && viewSelect()}
      </Grid>
      <Grid
        item
        container
        flexGrow={1}
        justifyContent={"flex-end"}
        flexWrap={"nowrap"}
        sx={{ padding: 1, paddingLeft: 0 }}
      >
        {customToolbar && customToolbar()}
        {rowDensityMenu && (
          <GridToolbarDensitySelector
            slotProps={{
              button: {
                color: "inherit",
                startIcon: null,
                sx: { minWidth: 40 },
              },
            }}
          />
        )}
        {exportMenu && (
          <GridToolbarExport
            slotProps={{
              button: {
                color: "inherit",
                startIcon: null,
                sx: { minWidth: 40 },
              },
            }}
          />
        )}
        {saveUserConfig && (
          <Tooltip
            followCursor
            title={
              isDefaultView
                ? "Currently in default view"
                : "This table view has been customized. Reset to default view"
            }
          >
            <span>
              <Button
                color={"inherit"}
                name={"Reset view"}
                disabled={isDefaultView}
                size="small"
                onClick={handleResetTableView}
                data-telemetry-name="Reset view button"
                sx={{
                  minWidth: theme.spacing(4),
                }}
              >
                <ResetIcon />
              </Button>
            </span>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}
